import Joi from "joi";
import { useState, useEffect } from "react";
import SweetAlert from "react-bootstrap-sweetalert";
import { connect, useDispatch } from "react-redux";
import {
  getUniversities,
  getCollegeAdmins,
  getCollegeTypes,
  getCollegeName,
} from "../../../../../action/common/commonActionCreator";
import { updateCollege } from "../../../../../action/masters/collegeActionCreator";
import FloatingLabelDropDown from "../../../../../common-components/floatingdropdown";
import FloatingLabelTextInput from "../../../../../common-components/floatingtextinput";
import {
  baseURL,
  cloneDeep,
  maxImageSize,
} from "../../../../../constants/commonConstants";
import { validateFields } from "../../../../../helper/joi-validation";
import {
  getAlertData,
  alertConstants,
} from "../../../../../helper/sweet-alert";
import {
  IEditCollege,
  ICollegeData,
} from "../../../../../models/master/college";
import { setCollege } from "../../../../../store/slices/master/collegeSlice";
import LoaderSpinner from "../../../../../common-components/loader";
import { UserRole } from "../../../../../constants/RoleEnum";
import { sortAlphabetically } from "../../../../../common-functions/commonFunctions";
import { collegeType } from "../../../../../constants/collegeType";
import { areObjectsEqual } from "../../../../../helper/generic-helpers";

const CollegeEdit = (props: IEditCollege) => {
  const dispatch = useDispatch();
  const {
    updateCollege,
    college,
    universities,
    getUniversities,
    setShowEdit,
    getAllColleges,
    isCollegeAdmin,
    getCollege,
    getLoginUsers,
    loginUsers,
    getCollegeTypes,
    collegeTypes,
    getCollegeName,
    collegeName,
    role,
  } = props;

  // states

  const [data, setData] = useState<ICollegeData>(cloneDeep(college));
  const [initialData, setInitialData] = useState<ICollegeData | undefined>(
    undefined
  );
  const [errors, setErrors] = useState<any>(undefined);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<any>("success");
  const [alertTitle, setAlertTitle] = useState<string>("Default");
  const [showAlertCancel, setShowAlertCancel] = useState<boolean>(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [name, setName] = useState<string>("");
  const [nameId, setNameId] = useState<number>(0);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [imageName, setImageName] = useState<string | null>(null);
  const [hasChange, setHasChange] = useState(false);

  // change the data at the time onchange fields
  const onChange = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    let _data = { ...data, [event.target.name]: event.target.value };

    if (event.target.name === "collegeType") {
      const selectedType = Number(event.target.value);
      getCollegeName(selectedType);
    }

    if (event.target.name === "collegeName") {
      let name = collegeName.find(
        (college) => college.id === event.target.value
      );
      setName(name?.name);
      setNameId(name?.id);
      _data.collegeName = event.target.value;
    }

    errors && delete errors[event.target.name];
    setData(_data);
  };

  const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    errors && delete errors[event.target.name];
    if (event.target.files && event.target.files.length > 0) {
      const selectedFile = event.target.files[0];
      setImageName(selectedFile.name);
      const fileType = selectedFile.type;
      const fileSize = selectedFile.size;
      const validFileTypes = [
        "image/png",
        "image/jpeg",
        "image/jpg",
        "image/webp",
        "image/ico",
        "image/svg+xml",
      ];
      if (!validFileTypes.includes(fileType)) {
        setErrors({
          ...errors,
          file: "Invalid file type. Please upload an image.",
        });
      } else if (fileSize > maxImageSize) {
        setErrors({
          ...errors,
          file: "File size exceeds 1MB. Please upload a small file.",
        });
        setImagePreview(null);
        setImageName(null);
      } else {
        setData({ ...data, [event.target.name]: selectedFile });
        const fileURL = URL.createObjectURL(selectedFile);
        setImagePreview(fileURL);
      }
    }
  };

  useEffect(() => {
    if (college) {
      let _college = cloneDeep(college);
      setData(_college);
      setInitialData(_college);
      getCollegeName(_college.collegeType);
      if (_college.collegeLogo) {
        const imageUrl = baseURL + _college.collegeLogo;
        setImagePreview(imageUrl);
        setImageName(imageUrl);
      }
      setNameId(college.externalId);
      setName(college.collegeName);
    }
  }, [college]);

  // setting sweet alert dynamically
  const setAlert = (type: string) => {
    let alertData = getAlertData(type);
    if (alertData) {
      setShowAlert(true);
      setAlertType(alertData.type);
      setAlertTitle(alertData.title);
    }
  };

  // executed on clicking 'OK' in sweet alert.
  const onConfirm = () => {
    if (alertType === alertConstants.error) {
      setShowAlert(false);
    } else {
      if (isCollegeAdmin) {
        getCollege && getCollege();
        setShowEdit(false);
      } else {
        getAllColleges && getAllColleges();
        setShowEdit(false);
      }
    }
  };

  const schema = Joi.object().keys({
    collegeName: Joi.alternatives().try(
      Joi.string().required().label("Select University Management"),
      Joi.number().greater(0).label("Select University Management")
    ),
    universityId: Joi.number().greater(0).required().label("Select University"),
    file: Joi.any().when("isFile", {
      is: true,
      then: Joi.any().optional(),
      otherwise: Joi.any().required().label("File is required"),
    }),
    description: Joi.string().required().label("Description is required"),
    location: Joi.string().required().label("Location is required"),
    address: Joi.string().required().label("Address is required"),
    collegeType: Joi.number()
      .greater(0)
      .required()
      .label("Select Management Type"),
  });

  const validationData = {
    ...data,
    isFile: Boolean(data?.file),
  };

  const onUpdate = async () => {
    const validationErrors = validateFields(validationData, schema);
    if (validationErrors === undefined) {
      const selectedUser = loginUsers.find(
        (user: any) => user.id === data.collegeLoginUserId
      );
      if (selectedUser) {
        data.collegeLoginUserName = selectedUser.name;
      }
      const formData = new FormData();
      formData.append("id", data.id?.toString() || "");
      formData.append("collegeName", name);
      formData.append("externalId", nameId.toString());
      formData.append("file", data.file);
      formData.append("description", data.description.replace(/\s+$/g, ""));
      formData.append("address", data.address);
      formData.append("location", data.location);
      formData.append("universityId", data.universityId.toString() || "");
      formData.append(
        "collegeLoginUserId",
        data.collegeLoginUserId != null
          ? data.collegeLoginUserId.toString()
          : ""
      );
      formData.append("collegeLoginUserName", data.collegeLoginUserName || "");
      formData.append("isActive", "true");
      formData.append("collegeType", data.collegeType.toString() || "");

      setLoading(true);
      setIsDisabled(true);
      try {
        await updateCollege(formData, data.id ?? 0, setAlert);
      } finally {
        setLoading(false);
        setIsDisabled(false);
      }
    } else {
      setErrors(validationErrors);
    }
  };

  useEffect(() => {
    if (data && initialData) {
      setHasChange(!areObjectsEqual(data, initialData));
    }
  }, [data, initialData]);

  useEffect(() => {
    getUniversities();
    getLoginUsers();
    getCollegeTypes();
  }, []);

  useEffect(() => {
    return () => {
      dispatch(setCollege(undefined));
    };
  }, []);

  return (
    <>
      {data && (
        <>
          <div className="modal-header">
            <h1 className="modal-title fs-5" id="addnewbuttonsLabel">
              Update University Management
            </h1>
            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
              onClick={() => setShowEdit(false)}
            ></button>
          </div>
          <div className="modal-body">
            <div className="row">
              <div className="col-md-12 form-group">
                <FloatingLabelDropDown
                  label={"University "}
                  onChange={onChange}
                  isMandatory={true}
                  value={data.universityId}
                  name={"universityId"}
                  optionsList={sortAlphabetically(universities)}
                  disable={role === UserRole.CollegeAdmin}
                />
                {errors?.universityId && (
                  <div className="text-danger">{errors.universityId}</div>
                )}
              </div>
              <div className="col-md-6 form-group">
                <FloatingLabelDropDown
                  label={"Management Type "}
                  onChange={onChange}
                  disable={role === UserRole.CollegeAdmin}
                  isMandatory={true}
                  value={data.collegeType}
                  name={"collegeType"}
                  optionsList={sortAlphabetically(collegeTypes)}
                />
                {errors?.collegeType && (
                  <div className="text-danger">{errors.collegeType}</div>
                )}
              </div>
              <div className="col-md-6 form-group">
                <FloatingLabelDropDown
                  label={"University Management Name "}
                  disable={role === UserRole.CollegeAdmin}
                  onChange={onChange}
                  isMandatory={true}
                  value={nameId}
                  name={"collegeName"}
                  optionsList={sortAlphabetically(collegeName || [])}
                />
                {errors?.collegeName && (
                  <div className="text-danger">{errors.collegeName}</div>
                )}
              </div>
              <div className="col-md-6 form-group">
                <FloatingLabelTextInput
                  label={"Address "}
                  onChange={onChange}
                  isMandatory={true}
                  disable={role === UserRole.CollegeAdmin}
                  value={data.address}
                  name={"address"}
                  maxLength={200}
                />
                {errors?.address && (
                  <div className="text-danger">{errors.address}</div>
                )}
              </div>
              <div className="col-md-6 form-group">
                <FloatingLabelTextInput
                  label={"Location "}
                  onChange={onChange}
                  disable={role === UserRole.CollegeAdmin}
                  isMandatory={true}
                  value={data.location}
                  name={"location"}
                  maxLength={50}
                />
                {errors?.location && (
                  <div className="text-danger">{errors.location}</div>
                )}
              </div>
              <div className="col-md-6 form-group">
                <label className="profile_details_text">
                  University Management Logo
                  <i className="label-asterisk"> * </i>
                </label>
                <div className="inputfile-flex">
                  {imagePreview && (
                    <div className="inputfile-img">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        style={{ width: "50px", height: "50px" }}
                      />
                    </div>
                  )}
                  <div className="inputfile-wrap">
                    <input
                      type="file"
                      name="file"
                      id="image"
                      className="form-control d-none"
                      required
                      onChange={onFileChange}
                      accept="image/png, image/jpeg, image/jpg, image/svg+xml, image/webp, image/x-icon, text/html"
                      value={errors?.file && ""}
                    />
                    <div className="inputfile">
                      <label htmlFor="image">Choose File</label>
                      <span>
                        {imageName
                          ? imageName.trim().split("/").pop()
                          : "No File Choosen"}
                      </span>
                    </div>
                  </div>
                </div>
                {errors?.file && (
                  <div className="text-danger">{errors.file}</div>
                )}
              </div>
              <div className="col-md-6 form-group">
                <FloatingLabelDropDown
                  label={"Login user ID "}
                  onChange={onChange}
                  isMandatory={false}
                  value={data.collegeLoginUserId}
                  name={"collegeLoginUserId"}
                  optionsList={sortAlphabetically(loginUsers)}
                  disable={
                    role === UserRole.CollegeAdmin ||
                    data?.collegeType == collegeType.universityAdministration
                  }
                />
                {errors?.collegeLoginUserId && (
                  <div className="text-danger">{errors.collegeLoginUserId}</div>
                )}
              </div>
              <div className="col-md-12 form-group">
                <label className="profile_details_text">
                  Description<i className="label-asterisk"> * </i>
                </label>
                <textarea
                  name="description"
                  value={data.description}
                  onChange={onChange}
                  maxLength={1000}
                ></textarea>
                {errors?.description && (
                  <div className="text-danger">{errors.description}</div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-primary"
                onClick={onUpdate}
                disabled={isDisabled || !hasChange}
              >
                Update
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
                onClick={() => setShowEdit(false)}
                disabled={isDisabled}
              >
                Close
              </button>
            </div>
          </div>
          <SweetAlert
            show={showAlert}
            success={false}
            type={alertType}
            title={alertTitle}
            onConfirm={() => onConfirm()}
            showCancel={showAlertCancel}
            onCancel={() => {
              setShowAlert(false);
              setShowAlertCancel(false);
            }}
            cancelBtnText={"No"}
            confirmBtnText={showAlertCancel ? "Yes" : "Ok"}
          ></SweetAlert>
          {loading && <LoaderSpinner />}
        </>
      )}
    </>
  );
};

const mapDispatchToProps = {
  updateCollege: (data: any, id: number, callBack: Function) =>
    updateCollege(data, id, callBack),
  getUniversities: () => getUniversities(),
  getLoginUsers: () => getCollegeAdmins(),
  getCollegeTypes: () => getCollegeTypes(),
  getCollegeName: (userType: number) => getCollegeName(userType),
};

function mapStateToProps(state: any) {
  return {
    universities: state.common.universities,
    loginUsers: state.common.loginUsers,
    collegeTypes: state.common.collegeTypes,
    collegeName: state.common.collegeName,
    role: state.userDetails.userProfileData?.currentRole,
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(CollegeEdit);
